import React from 'react';
import {Label} from 'reactstrap';
import {AvGroup} from 'availity-reactstrap-validation';
import moment from 'moment';
import AvField from '../../shared/component/form/AvField';
import ReadOnlyField from '../../shared/component/form/ReadOnlyField';
import GeneralUtils from '../../shared/utils/GeneralUtils';
import {BASKET_STATUS, BASKET_TYPE, ERROR_TYPE, ORDER_STATUS, ORDER_TYPE} from '../../shared/utils/Constants';
import {formatPaymentTypeLabel} from '../order/orderPlacedUtils';

const lookupConstant = (list, value) => {
    if (GeneralUtils.isNullOrEmpty(value)) {
        return '-';
    }
    const match = list.find((item) => item.value === value);
    return match ? match.name : value;
};

const formatOrderDate = (value) => {
    if (GeneralUtils.isNullOrEmpty(value)) {
        return '-';
    }
    const parsed = moment(value, [
        'DD/MM/YYYY HH:mm',
        'D/M/YYYY HH:mm',
        'DD/M/YYYY HH:mm',
        'D/MM/YYYY HH:mm',
        moment.ISO_8601,
    ], true);
    const formatted = parsed.isValid() ? parsed : moment(value);
    return formatted.isValid()
        ? formatted.format('DD/MM/YYYY HH:mm').replace(' ', '\u00A0')
        : '-';
};

const inlineField = (label, value, className = '') => (
    <ReadOnlyField key={label} label={label} value={value} layout="inline" className={className}/>
);

const normalizeOrderType = (orderType) => {
    if (GeneralUtils.isNullOrEmpty(orderType)) {
        return orderType;
    }
    const map = {
        'Table Order': 'EAT_IN',
        'Delivery': 'DELIVERY',
        'Collection': 'COLLECTION',
    };
    return map[orderType] || orderType;
};

const buildOrderNoTypeValue = (order, showBasketType) => {
    const parts = [
        order.orderNo,
        lookupConstant(ORDER_TYPE, normalizeOrderType(order.orderType)),
    ];

    if (showBasketType) {
        parts.push(lookupConstant(BASKET_TYPE, order.basketType));
    }

    return parts.filter((part) => part && part !== '-').join(' / ');
};

const hasDisplayValue = (value) => {
    if (GeneralUtils.isNullOrEmpty(value)) {
        return false;
    }
    const normalized = String(value).trim();
    return normalized !== '' && normalized !== '-';
};

const getMemberAddressLabel = (orderType) => {
    const normalized = normalizeOrderType(orderType);
    if (normalized === 'EAT_IN') {
        return 'Table';
    }
    if (normalized === 'DELIVERY') {
        return 'Delivery To';
    }
    if (normalized === 'COLLECTION') {
        return 'Collection';
    }
    return 'Address';
};

const getMemberLocationValue = (order) => order.deliveryAddress;

const shouldShowMemberLocation = (order) => shouldShowAddress(order);

const isAsSoonAsPossibleWhen = (value) => {
    if (!hasDisplayValue(value)) {
        return true;
    }
    return /^as soon as possible$/i.test(String(value).trim());
};

const formatReadyOrDeliverBy = (order) => {
    if (!GeneralUtils.isNullOrEmpty(order.deliveryDate)) {
        return formatOrderDate(order.deliveryDate);
    }
    const raw = order.deliveryDateString;
    if (GeneralUtils.isNullOrEmpty(raw)) {
        return '-';
    }
    if (isAsSoonAsPossibleWhen(raw)) {
        return raw;
    }
    const formatted = formatOrderDate(raw);
    return formatted !== '-' ? formatted : raw;
};

const hasScheduledWhen = (order) => {
    if (!GeneralUtils.isNullOrEmpty(order.deliveryDate)) {
        return true;
    }
    return !isAsSoonAsPossibleWhen(order.deliveryDateString);
};

const getScheduledWhenValue = (order) => {
    if (!GeneralUtils.isNullOrEmpty(order.deliveryDate)) {
        return formatOrderDate(order.deliveryDate);
    }
    if (!isAsSoonAsPossibleWhen(order.deliveryDateString)) {
        const formatted = formatOrderDate(order.deliveryDateString);
        return formatted !== '-' ? formatted : order.deliveryDateString;
    }
    return null;
};

const shouldShowScheduledWhen = (order) => {
    const orderType = normalizeOrderType(order.orderType);
    if (orderType !== 'DELIVERY' && orderType !== 'COLLECTION') {
        return false;
    }
    return hasScheduledWhen(order);
};

const getScheduledWhenLabel = (orderType) => (
    orderType === 'DELIVERY' ? 'Deliver by' : 'Ready by'
);

const shouldShowAddress = (order) => {
    const orderType = normalizeOrderType(order.orderType);
    if (orderType === 'COLLECTION') {
        return false;
    }
    if (orderType !== 'DELIVERY' && orderType !== 'EAT_IN') {
        return false;
    }
    return hasDisplayValue(order.deliveryAddress);
};

const hasMoneyDisplayValue = (value) => {
    if (GeneralUtils.isNullOrEmpty(value)) {
        return false;
    }
    const normalized = String(value).replace(/[£,\s]/g, '');
    const amount = Number(normalized);
    return Number.isFinite(amount) && amount !== 0;
};

export function OrderDetailTabFields({order, showBasketType, variant, driverList, onDriverChange}) {
    const showDeliveryRow = order.orderType === 'DELIVERY' || order.orderType === 'COLLECTION';
    const showDriver = (order.orderStatus === 'ORDER_READY' && order.orderType === 'DELIVERY')
        || !GeneralUtils.isNullOrEmpty(order.driverId);

    const driverDisabled = variant === 'history'
        ? !GeneralUtils.isNullOrEmpty(order.driverId)
        : !(GeneralUtils.isNullOrEmpty(order.driverId)
            || (order.orderStatus === 'ORDER_READY' && order.orderType === 'DELIVERY'));

    const driverIdKey = variant === 'history' ? 'id' : 'userId';
    const hasOrderNotes = hasDisplayValue(order.orderNotes);
    const hasAllergies = hasDisplayValue(order.allergies);

    const priceFields = [
        inlineField('Total Price', order.totalPriceString),
        inlineField('Service Charge', order.calculatedServiceChargeString),
        inlineField('Discount', order.manualDiscountString),
    ];

    if (order.orderType === 'DELIVERY') {
        priceFields.push(inlineField('Delivery Fee', order.calculatedDeliveryFeeString));
    }

    return (
        <div className="order-detail-fields-split">
            <div className="order-detail-fields-split__main order-detail-fields-split__main--grid">
                {inlineField('Order No/ Type', buildOrderNoTypeValue(order, showBasketType), 'order-detail-field--full')}

                <div className="order-detail-meta-grid order-detail-field--full">
                    {inlineField('Status', lookupConstant(BASKET_STATUS, order.basketStatus), 'order-detail-meta-grid__col-1 order-detail-meta-grid__row-1')}
                    {inlineField('Order Status', lookupConstant(ORDER_STATUS, order.orderStatus), 'order-detail-meta-grid__col-2 order-detail-meta-grid__row-1')}
                    {inlineField('Sync Status', order.syncStatus, 'order-detail-meta-grid__col-3 order-detail-meta-grid__row-1')}

                    {showDeliveryRow ? (
                        <>
                            {inlineField(
                                order.orderType === 'DELIVERY' ? 'Deliver by' : 'Ready by',
                                formatReadyOrDeliverBy(order),
                                'order-detail-meta-grid__col-1 order-detail-meta-grid__row-2'
                            )}
                            {inlineField('Order Date', formatOrderDate(order.orderDate), 'order-detail-meta-grid__col-2 order-detail-meta-grid__row-2')}
                        </>
                    ) : (
                        inlineField('Order Date', formatOrderDate(order.orderDate), 'order-detail-meta-grid__col-1 order-detail-meta-grid__row-2')
                    )}
                </div>

                {!GeneralUtils.isNullOrEmpty(order.customerFullNameWithPhone) && (
                    inlineField('Customer', order.customerFullNameWithPhone, 'order-detail-field--full')
                )}

                {shouldShowAddress(order) && inlineField('Address', order.deliveryAddress, 'order-detail-field--full')}
                {hasOrderNotes && inlineField('Order Notes', order.orderNotes, !hasAllergies ? 'order-detail-field--full' : '')}
                {hasAllergies && inlineField('Allergies', order.allergies, !hasOrderNotes ? 'order-detail-field--full' : '')}

                {showDriver && (
                    <div className="order-detail-fields-split__driver order-detail-field--full">
                        <AvGroup>
                            <Label for="driverId">
                                Driver
                                {variant === 'history' && <span style={{color: 'red'}}> (*)</span>}
                            </Label>
                            <AvField
                                type="select"
                                disabled={driverDisabled}
                                className="form-control"
                                name="driverId"
                                value={order.driverId}
                                onChange={onDriverChange}
                            >
                                <option value="" key=""/>
                                {(driverList || []).map((element) => (
                                    <option value={element[driverIdKey]} key={element[driverIdKey]}>
                                        {element.fullName}
                                    </option>
                                ))}
                            </AvField>
                        </AvGroup>
                    </div>
                )}
            </div>
            <div className="order-detail-fields-split__divider" aria-hidden="true"/>
            <div className="order-detail-fields-split__prices order-detail-fields-split__prices--aligned">
                {priceFields}
            </div>
        </div>
    );
}

export function OrderMoreTabFields({order}) {
    return (
        <div className="order-detail-fields-split order-detail-fields-split--more">
            <div className="order-detail-fields-split__main order-detail-fields-split__main--grid">
                {inlineField('Accepted Date', formatOrderDate(order.acceptedDate))}
                {inlineField('Preparing Date', formatOrderDate(order.preparingDate))}
                {inlineField('Ready Date', formatOrderDate(order.readyDate))}
                {inlineField('Delivered Date', formatOrderDate(order.deliveredDate))}
            </div>
            <div className="order-detail-fields-split__divider" aria-hidden="true"/>
            <div className="order-detail-fields-split__prices order-detail-fields-split__prices--aligned">
                {inlineField('Items Total', order.subTotalPriceString)}
                {inlineField('Service Charge', order.calculatedServiceChargeString)}
                {order.orderType === 'DELIVERY' && inlineField('Delivery Fee', order.calculatedDeliveryFeeString)}
            </div>
        </div>
    );
}

export function OrderErrorFields({order}) {
    return (
        <div className="order-detail-fields-split__main order-detail-fields-split__main--grid">
            {inlineField('Error Type', lookupConstant(ERROR_TYPE, order.errorType), 'order-detail-field--full')}
            {inlineField('Error Message', order.errorMessage, 'order-detail-field--full')}
        </div>
    );
}

/** Member-facing order summary (confirmation screen, My Orders popup). */
export function MemberOrderSummaryFields({
    order,
    showPrices = true,
    showPaymentType = false,
    showTradingName = false,
}) {
    if (!order) {
        return null;
    }

    const orderType = normalizeOrderType(order.orderType);
    const hasOrderNotes = hasDisplayValue(order.orderNotes);
    const hasAllergies = hasDisplayValue(order.allergies);
    const tradingName = showTradingName
        ? (order.tradingName || GeneralUtils.getMemberStoreTradingName())
        : null;
    const paymentDisplay = showPaymentType
        ? (order.paymentTypeDisplay || order.paymentLabel || formatPaymentTypeLabel(order.paymentType))
        : null;
    const locationValue = getMemberLocationValue(order);

    const priceFields = [];
    if (showPrices) {
        if (!GeneralUtils.isNullOrEmpty(order.subTotalPriceString)) {
            priceFields.push(inlineField('Items Total', order.subTotalPriceString));
        } else if (!GeneralUtils.isNullOrEmpty(order.totalPriceString)) {
            priceFields.push(inlineField('Items Total', order.totalPriceString));
        }
        if (hasMoneyDisplayValue(order.calculatedServiceChargeString)) {
            priceFields.push(inlineField('Service Charge', order.calculatedServiceChargeString));
        }
        if (orderType === 'DELIVERY' && hasMoneyDisplayValue(order.calculatedDeliveryFeeString)) {
            priceFields.push(inlineField('Delivery Fee', order.calculatedDeliveryFeeString));
        }
        if (hasMoneyDisplayValue(order.manualDiscountString)) {
            priceFields.push(inlineField('Discount', order.manualDiscountString));
        }
        if (!GeneralUtils.isNullOrEmpty(order.totalPriceString)) {
            priceFields.push(inlineField('Total', order.totalPriceString, 'member-order-summary__total'));
        }
    }

    return (
        <div className="order-detail-fields-split order-detail-fields-split--member">
            <div className="order-detail-fields-split__main order-detail-fields-split__main--grid">
                {inlineField('Order No/ Type', buildOrderNoTypeValue(order, false), 'order-detail-field--full')}

                <div className="order-detail-meta-grid order-detail-meta-grid--member order-detail-field--full">
                    {hasDisplayValue(tradingName) && inlineField(
                        'Store',
                        tradingName,
                        'order-detail-field--full member-order-summary__meta-value'
                    )}
                    {inlineField(
                        'Order Date',
                        formatOrderDate(order.orderDate),
                        'order-detail-field--full member-order-summary__meta-value'
                    )}
                    {shouldShowScheduledWhen(order) && inlineField(
                        getScheduledWhenLabel(orderType),
                        getScheduledWhenValue(order),
                        'order-detail-field--full member-order-summary__meta-value'
                    )}
                    {hasDisplayValue(paymentDisplay) && inlineField(
                        'Payment',
                        paymentDisplay,
                        'order-detail-field--full member-order-summary__meta-value'
                    )}
                </div>

                {shouldShowMemberLocation(order) && hasDisplayValue(locationValue) && inlineField(
                    getMemberAddressLabel(order.orderType),
                    locationValue,
                    'order-detail-field--full member-order-summary__address'
                )}
                {hasOrderNotes && inlineField('Order Notes', order.orderNotes, !hasAllergies ? 'order-detail-field--full' : '')}
                {hasAllergies && inlineField('Allergies', order.allergies, !hasOrderNotes ? 'order-detail-field--full' : '')}
            </div>

            {showPrices && priceFields.length > 0 && (
                <>
                    <div className="order-detail-fields-split__divider" aria-hidden="true"/>
                    <div className="order-detail-fields-split__prices member-order-summary__prices">
                        {priceFields}
                    </div>
                </>
            )}
        </div>
    );
}
